import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type JSX } from 'react'

import { cn } from '../lib/cn.ts'
import {
  DISPLAY_REFRESH_MS,
  currentDisplayPage,
  getDisplayClock,
  type DisplaySession
} from '../lib/display-mode.ts'
import {
  clockLabel,
  isStale,
  newestUpdate,
  oldestUpdate,
  type HeaderQuery,
  progressShare,
  updatedLabel
} from '../lib/tv-header.ts'
import { useTimeZone } from './time-zone-provider.tsx'

/** How often the bar redraws its clock, its age and its progress. */
const TICK_MS = 1000

/** What the bar reads each second. */
interface Reading {
  now: number
  /** The newest answer, which the age prints. */
  updatedAt: number | null
  /** The oldest answer, which the stale flag reads. */
  oldestAt: number | null
}

/**
 * Read the clock and the cache.
 *
 * @param client - The query client.
 * @returns The reading.
 */
function read(client: QueryClient): Reading {
  const queries: HeaderQuery[] = client
    .getQueryCache()
    .getAll()
    .map(query => ({
      dataUpdatedAt: query.state.dataUpdatedAt,
      observed: query.getObserversCount() > 0,
      quiet: query.meta?.quiet === true
    }))

  return {
    now: getDisplayClock().now(),
    updatedAt: newestUpdate(queries),
    oldestAt: oldestUpdate(queries)
  }
}

/**
 * The clock and the data age, read again each second.
 *
 * The display clock drives it, so a test can move time. The cache is read in
 * the tick, not in render, so the compiler cannot keep an old answer.
 *
 * @returns The clock reading and the newest answer time.
 */
function useReading(): Reading {
  const client = useQueryClient()
  const [reading, setReading] = useState<Reading>(() => read(client))

  useEffect(() => {
    let cancel = (): void => {}
    const schedule = (): void => {
      cancel = getDisplayClock().after(TICK_MS, () => {
        setReading(read(client))
        schedule()
      })
    }
    schedule()

    return () => {
      cancel()
    }
  }, [client])

  return reading
}

/** Keep the visual fill on the compositor while the accessible value follows the clock. */
function ProgressLine({
  shownAt,
  seconds,
  share
}: {
  shownAt: number | null
  seconds: number | null
  share: number
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const animated =
    typeof globalThis.HTMLElement?.prototype.animate === 'function' &&
    !globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  useEffect(() => {
    if (shownAt === null || seconds === null || !animated) return undefined
    const animation = ref.current?.animate?.(
      [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
      {
        duration: seconds * 1000,
        delay: Math.min(0, shownAt - getDisplayClock().now()),
        fill: 'both'
      }
    )
    return () => {
      animation?.cancel()
    }
  }, [shownAt, seconds, animated])
  return (
    <div
      ref={ref}
      data-testid="tv-header-progress"
      role="progressbar"
      aria-label="Time on this page"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(share * 100)}
      className="bg-primary h-full origin-left"
      style={{ transform: `scaleX(${animated ? 0 : share})` }}
    />
  )
}

/**
 * TvHeaderBar — the thin bar above each page on an office TV.
 *
 * It says how old the page's data is, shows the office time, and
 * fills a progress line over the page's time on screen. The age is the newest
 * answer. When the oldest answer is past the stale limit, the age shows in the
 * warning ink.
 *
 * @param props - Bar props.
 * @param props.session - The display session.
 * @returns The bar.
 */
export function TvHeaderBar({
  session
}: {
  session: DisplaySession
}): JSX.Element {
  const { now, updatedAt, oldestAt } = useReading()
  const { timeZone } = useTimeZone()
  const page = currentDisplayPage(session)
  const shownAt =
    session.kind === 'playing' || session.kind === 'refreshing'
      ? session.shownAt
      : null
  const share =
    page === null || shownAt === null
      ? 0
      : progressShare(shownAt, page.seconds, now)
  const stale = oldestAt !== null && isStale(oldestAt, now, DISPLAY_REFRESH_MS)

  return (
    <header data-testid="tv-header" className="grid gap-1">
      <div
        data-testid="tv-header-status"
        className="text-muted-foreground flex items-baseline justify-end gap-6 tabular-nums"
      >
        {updatedAt === null ? null : (
          <span
            data-testid="tv-header-updated"
            data-stale={stale ? 'true' : 'false'}
            className={cn(stale && 'text-warning-ink font-semibold')}
          >
            {updatedLabel(updatedAt, now)}
          </span>
        )}
        <span data-testid="tv-header-clock" className="type-section-title">
          {clockLabel(now, timeZone)}
        </span>
      </div>
      <div className="bg-border h-(--rule-width)">
        <ProgressLine
          key={shownAt ?? 'none'}
          shownAt={shownAt}
          seconds={page?.seconds ?? null}
          share={share}
        />
      </div>
    </header>
  )
}
